/**
 * SailPoint Group Authorization Configuration
 * 
 * This file defines which SailPoint groups are authorized to access 
 * the MI Revenue Analytics Platform.
 * 
 * Update the group names below according to your organization's 
 * SailPoint/OKTA group configuration.
 */

export interface AuthorizationConfig {
  authorizedGroups: string[];
  groupValidationEnabled: boolean;
  allowEmptyGroups: boolean; // For development/testing only
}

// Environment-specific group configurations
export const authorizationConfig: AuthorizationConfig = {
  // List of SailPoint groups that are authorized to access the application
  authorizedGroups: [
    // Production group
    'GRP_REV_LANDING_PAGE_USERS',
    
    // Development group (includes dev access)
    'GRP_REV_LANDING_PAGE_DEV_USERS',
  ],

  // Set to true to enforce group validation
  groupValidationEnabled: true,

  // Set to true only for development/testing (bypasses empty group validation)
  allowEmptyGroups: false,
};

/**
 * Helper function to check if a user has authorization
 * @param userGroups Array of groups from user's OKTA profile
 * @returns Object with authorization status and error message
 */
export const validateUserGroups = (userGroups: string[]): { 
  isAuthorized: boolean; 
  error: string | null;
  matchedGroups: string[];
} => {
  // If group validation is disabled, allow all authenticated users
  if (!authorizationConfig.groupValidationEnabled) {
    return { isAuthorized: true, error: null, matchedGroups: userGroups };
  }

  // Check for empty groups
  if (!userGroups || userGroups.length === 0) {
    if (authorizationConfig.allowEmptyGroups) {
      return { isAuthorized: true, error: null, matchedGroups: [] };
    }
    
    return {
      isAuthorized: false,
      error: 'No group memberships found. Please contact your administrator to be added to the appropriate SailPoint groups.',
      matchedGroups: []
    };
  }

  // Find matching authorized groups
  const matchedGroups = userGroups.filter(userGroup => 
    authorizationConfig.authorizedGroups.includes(userGroup)
  );

  if (matchedGroups.length === 0) {
    return {
      isAuthorized: false,
      error: `Access denied. You must be a member of one of these groups: ${authorizationConfig.authorizedGroups.join(', ')}. Please contact your administrator.`,
      matchedGroups: []
    };
  }

  return { isAuthorized: true, error: null, matchedGroups };
};

/**
 * Get user-friendly group descriptions for display purposes
 */
export const getGroupDescriptions = (): Record<string, string> => {
  return {
    'GRP_REV_LANDING_PAGE_USERS': 'Revenue Landing Page - Production Users',
    'GRP_REV_LANDING_PAGE_DEV_USERS': 'Revenue Landing Page - Development Users',
  };
};

export default authorizationConfig;